import { useMutation } from "@tanstack/react-query"
import { queryClient } from "../index"
import { newArchAxiosInstance } from "utils/request"
import {
  fetchUserSession,
  HAS_LOGGED_IN_BEFORE,
  ILoginResponse,
  ILoginWith2FA,
  ISendCodeResponse,
  IUser,
  useAuthStore,
  UserExtension,
} from "stores/auth"
import { getRelativePathFromUrl, onQueryError } from "utils/utils"
import { useAuthModalStore } from "stores/auth-modal"
import { useWhatsNewStore } from "stores/whats-new"
import { playBootSplash } from "bootMode"
import { useLocation, useNavigate } from "react-router-dom"
import { useQuery } from "./useQuery"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import { customerIOTrackEvent } from "utils/customerio"
import { useState } from "react"
import { useAppNotificationStore } from "stores"

const markLoggedIn = () => {
  try {
    sessionStorage.setItem(HAS_LOGGED_IN_BEFORE, "true")
  } catch (_) {
    /* ignore */
  }
}

export interface LoginInput {
  email?: string
  password?: string
  token?: string
  auth_code?: string
  provider?: string
}

export const useAuth = () => {
  const [showVerifyOtpPrompt, setShowVerifyOtpPrompt] = useState(false)
  const [twoFALoginResponse, setTwoFALoginResponse] = useState<ILoginWith2FA>()
  const [showAdminEmailOtpPrompt, setShowAdminEmailOtpPrompt] = useState(false)
  const [adminSetupLoginResponse, setAdminSetupLoginResponse] =
    useState<ILoginWith2FA>()
  const [adminSetupLoginInput, setAdminSetupLoginInput] = useState<LoginInput>()
  const [showResetPasswordPrompt, setShowResetPasswordPrompt] = useState(false)
  const [resetPasswordLoginResponse, setResetPasswordLoginResponse] =
    useState<ILoginWith2FA>()
  const [resetPasswordLoginInput, setResetPasswordLoginInput] =
    useState<LoginInput>()
  const { toast } = useAppNotificationStore()

  const { close: closeAuthModal, isOpen: isAuthModalOpen } = useAuthModalStore(
    (state) => ({
      open: state.open,
      close: state.close,
      isOpen: state.isOpen,
    })
  )

  const query = useQuery(useLocation().search)
  const navigate = useNavigate()

  const navigateAfterAuth = (user: (IUser & Partial<UserExtension>) | null) => {
    if (isAuthModalOpen) {
      closeAuthModal()
      return
    }

    // Signing in is when the app is entered, so the logo plays here -- over whatever the
    // destination draws while it loads. Not for the session modal above: that person never
    // left the app.
    playBootSplash()
    // Same reasoning: "What's New" greets a sign-in, not a refresh or a re-auth.
    useWhatsNewStore.getState().requestAfterLogin()

    const redirect = query.get("redirect")
    if (redirect && !redirect.includes("login")) {
      const redirectUrl = new URL(redirect)
      navigate(redirectUrl.pathname + redirectUrl.search, { replace: true })
      return
    }
    if (user?.isAdmin === true) {
      if (user.setupPercentage === 100 || user?.setupStatus === "Approved") {
        navigate("/dashboard")
      } else {
        navigate("/dashboard/company-onboarding")
      }
    } else if (user?.userStatusId === 2 || user?.userStatusId === 3) {
      navigate("/employee/susp-exit/payslips")
    } else if (user?.userStatusId === 1) {
      navigate("/employee/dashboard/")
    } else {
      navigate("/employee/dashboard/welcome")
    }
    mixPanelEvent("auth-sign-in", String(user?.userName) || "")
    customerIOTrackEvent("Webapp Login", String(user?.userName), {
      email: String(user?.userName),
    })
  }

  const onLoginSuccess = async (data: {
    access_token?: string | null
    refresh_token?: string | null
    userid?: string | null
  }) => {
    const { access_token, refresh_token, userid } = data || {}

    useAuthStore.setState({
      authToken: access_token,
      refreshToken: refresh_token,
      authUserId: userid,
      isAuthenticated: true,
    })
    markLoggedIn()
    const { user } = await fetchUserSession()
    navigateAfterAuth(user)
  }

  const { mutate: login, isLoading: isLoggingIn } = useMutation({
    mutationFn: async ({
      email,
      password,
      auth_code,
      provider,
    }: LoginInput) => {
      useAuthStore.getState().clearAuthDataStorage()
      const res = await newArchAxiosInstance.post<ILoginResponse>(
        "/identity/authenticate",
        { username: email, password, auth_code, provider }
      )
      return res.data
    },
    onSuccess: async (data, variables) => {
      const {
        access_token,
        refresh_token,
        status,
        userid: auth_user_id,
        callback_url,
        is_generated,
        message,
      } = data?.data || {}
      const { token } = variables

      if (is_generated && callback_url) {
        const callback = getRelativePathFromUrl(callback_url)
        window.location.href = callback
      } else if (status === "TwoFactorSetupRequired") {
        setShowAdminEmailOtpPrompt(true)
        setAdminSetupLoginResponse({
          access_token,
          status,
          message,
          userid: auth_user_id,
        })
        setAdminSetupLoginInput(variables)
      } else if (status === "TwoFactorRequired") {
        setShowVerifyOtpPrompt(true)
        setTwoFALoginResponse({
          access_token,
          refresh_token,
          status,
          message,
          userid: auth_user_id,
        })
      } else if (status === "PasswordResetRequired") {
        setShowResetPasswordPrompt(true)
        setResetPasswordLoginResponse({
          access_token,
          status,
          message,
          userid: auth_user_id,
        })
        setResetPasswordLoginInput(variables)
      } else if (token !== undefined) {
        useAuthStore.setState({
          authToken: token,
          isAuthenticated: true,
        })
        markLoggedIn()
        const { user } = await fetchUserSession()
        if (!user?.isAdmin) {
          useAuthStore.getState().clearAuthDataStorage()
        } else {
          navigateAfterAuth(user)
        }
      } else {
        await onLoginSuccess({
          access_token,
          refresh_token,
          userid: auth_user_id,
        })

        try {
          queryClient.refetchQueries({
            type: "all",
            predicate: (q) => q.state.status === "error",
          })
        } catch (_) {
          // noop
        }
      }
    },
    onError: onQueryError,
  })

  const { mutate: verify2FA, isLoading: isVerifying2FA } = useMutation({
    mutationFn: async ({
      code,
      generated,
    }: {
      code: string
      generated: boolean
    }) => {
      const accessToken = twoFALoginResponse?.access_token ?? ""
      const res = await newArchAxiosInstance.post<ILoginResponse>(
        "/account/2fa/verifycode",
        { generated, code },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      )
      return res.data
    },
    onSuccess: async (data) => {
      const { access_token, refresh_token, userid } = data?.data || {}

      await onLoginSuccess({ access_token, refresh_token, userid })
      setShowVerifyOtpPrompt(false)
    },
    onError: onQueryError,
  })

  const { mutateAsync: sendCode, isLoading: isSendingCode } = useMutation({
    mutationFn: async () => {
      const accessToken = twoFALoginResponse?.access_token ?? ""
      const res = await newArchAxiosInstance.post<ISendCodeResponse>(
        "/account/2fa/sendcode",
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      )
      return res.data
    },
    onSuccess: (data) => {
      toast.success(data?.data || data?.message)
    },
    onError: onQueryError,
  })

  const { mutate: verifyAdminEmailOtp, isLoading: isVerifyingAdminOtp } =
    useMutation({
      mutationFn: async ({ code }: { code: string }) => {
        const accessToken = adminSetupLoginResponse?.access_token ?? ""
        const res = await newArchAxiosInstance.post<ILoginResponse>(
          "/account/2fa/verifyadmincode",
          { code },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        )
        return res.data
      },
      onSuccess: async (data) => {
        const { access_token } = data?.data || {}
        useAuthStore.setState({ setup2FAToken: access_token })
        setShowAdminEmailOtpPrompt(false)
        navigate("/auth/2fa-setup")
      },
      onError: onQueryError,
    })

  const resendAdminEmailCode = () => {
    if (adminSetupLoginInput) {
      login(adminSetupLoginInput)
    }
  }

  const dismissAdminEmailOtpPrompt = () => {
    setShowAdminEmailOtpPrompt(false)
    setAdminSetupLoginResponse(undefined)
    setAdminSetupLoginInput(undefined)
  }

  const {
    mutate: completePasswordReset,
    isLoading: isCompletingPasswordReset,
  } = useMutation({
    mutationFn: async ({
      code,
      currentPassword,
      newPassword,
    }: {
      code: string
      currentPassword: string
      newPassword: string
    }) => {
      const accessToken = resetPasswordLoginResponse?.access_token ?? ""
      const res = await newArchAxiosInstance.post<ILoginResponse>(
        "/account/completepasswordreset",
        { currentPassword, newPassword, code },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      )
      return res.data
    },
    onSuccess: async (data) => {
      const { access_token, refresh_token, userid } = data?.data || {}
      await onLoginSuccess({ access_token, refresh_token, userid })
      setShowResetPasswordPrompt(false)
      setResetPasswordLoginResponse(undefined)
      setResetPasswordLoginInput(undefined)
    },
    onError: onQueryError,
  })

  const resendPasswordResetCode = () => {
    if (resetPasswordLoginInput) {
      login(resetPasswordLoginInput)
    }
  }

  const dismissResetPasswordPrompt = () => {
    setShowResetPasswordPrompt(false)
    setResetPasswordLoginResponse(undefined)
    setResetPasswordLoginInput(undefined)
  }

  const loginWithCredentials = (
    email: string,
    password: string,
    token?: string
  ) => {
    return login({ email, password, token })
  }

  const loginWithToken = (token: string) => {
    return login({ token })
  }

  const loginWithProvider = (provider: string, auth_code: string) => {
    return login({ provider, auth_code })
  }

  /**
   * @param access_token - access token
   * @param newUser - new user object
   * @returns {Promise<{user: Partial<IUser>}>}
   * @description - change organization function lets you switch between companies
   */
  const changeOrganization = (
    access_token: string,
    newUser: IUser & Partial<UserExtension>
  ) => {
    useAuthStore.setState({
      authToken: access_token,
      isAuthenticated: true,
      user: newUser,
    })
  }

  const extendUser = (userData: Partial<UserExtension>) => {
    useAuthStore.setState((state) => ({
      user: { ...state.user, ...userData } as IUser & Partial<UserExtension>,
    }))
  }

  const loginWithSSO = async (
    token: string,
    code: string,
    referrer: string
  ) => {
    useAuthStore.getState().clearAuthDataStorage()

    useAuthStore.setState({
      authToken: token,
      isAuthenticated: true,
      user: null,
      authUserId: code,
      referrer,
    })

    const { user } = await fetchUserSession()
    if (user?.isAdmin === undefined) {
      useAuthStore.getState().clearAuthDataStorage()
      navigate("/auth/login")
    } else {
      navigateAfterAuth(user)
    }
  }

  return {
    loginWithCredentials,
    loginWithToken,
    loginWithProvider,
    verify2FA,
    sendCode,
    isSendingCode,
    showVerifyOtpPrompt,
    twoFALoginResponse,
    isLoggingIn,
    isVerifying2FA,
    changeOrganization,
    extendUser,
    loginWithSSO,
    navigateAfterAuth,
    onLoginSuccess,
    showAdminEmailOtpPrompt,
    adminSetupLoginResponse,
    verifyAdminEmailOtp,
    isVerifyingAdminOtp,
    resendAdminEmailCode,
    dismissAdminEmailOtpPrompt,
    showResetPasswordPrompt,
    resetPasswordLoginResponse,
    completePasswordReset,
    isCompletingPasswordReset,
    resendPasswordResetCode,
    dismissResetPasswordPrompt,
  }
}
